import type { AvatarOnlySessionManager } from './core/AvatarOnlySessionManager';
import type { AvatarAudioPacingConfig, LiveAvatarTrackingContext } from './types';
import { isLiveAvatarDevMode } from './api/liveavatarApi';

const DEFAULT_AUDIO_PACING: AvatarAudioPacingConfig = {
  minChunkSendMs: 70,
  maxChunkSendMs: 220,
  schedulerResetDriftMs: 400,
};
const LIVEAVATAR_ORIGIN = 'https://api.liveavatar.com';
const AVATAR_PORTRAIT_URL = '/sally-avatar-portrait.jpg';

let avatarSessionManager: AvatarOnlySessionManager | null = null;
let avatarSessionManagerPromise: Promise<AvatarOnlySessionManager> | null = null;
let pendingTrackingContext: LiveAvatarTrackingContext = {};
const pendingSpeakingListeners = new Map<(isSpeaking: boolean) => void, () => void>();

const loadAvatarRuntime = async (): Promise<AvatarOnlySessionManager> => {
  if (avatarSessionManager) {
    return avatarSessionManager;
  }
  if (!avatarSessionManagerPromise) {
    avatarSessionManagerPromise = import('./core/AvatarOnlySessionManager').then(({ AvatarOnlySessionManager }) => {
      const manager = new AvatarOnlySessionManager();
      manager.setTrackingContext(pendingTrackingContext);
      for (const listener of Array.from(pendingSpeakingListeners.keys())) {
        pendingSpeakingListeners.set(listener, manager.subscribeSpeakingState(listener));
      }
      avatarSessionManager = manager;
      return manager;
    });
  }
  return avatarSessionManagerPromise;
};

const addResourceHint = (rel: 'dns-prefetch' | 'preconnect', href: string): void => {
  if (typeof document === 'undefined' || document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (rel === 'preconnect') {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
};

/**
 * Download and parse only free browser resources. This never requests a token
 * and never starts a billable LiveAvatar session.
 */
export const preloadAvatarRuntime = (): void => {
  addResourceHint('dns-prefetch', LIVEAVATAR_ORIGIN);
  addResourceHint('preconnect', LIVEAVATAR_ORIGIN);
  if (typeof Image !== 'undefined') {
    const portrait = new Image();
    portrait.decoding = 'async';
    portrait.src = AVATAR_PORTRAIT_URL;
  }
  void loadAvatarRuntime().catch((error) => {
    console.warn('[LiveAvatar] free runtime preload failed:', error);
  });
};

export const startAvatarSession = async (context?: LiveAvatarTrackingContext): Promise<void> => {
  const manager = await loadAvatarRuntime();
  await manager.start(context);
};

export const getAvatarSessionStatus = () => avatarSessionManager?.getStatus() || 'idle';

export const setAvatarTrackingContext = (context: LiveAvatarTrackingContext): void => {
  pendingTrackingContext = { ...pendingTrackingContext, ...context };
  if (
    Object.prototype.hasOwnProperty.call(context, 'avatar_profile_slug')
    && !context.avatar_profile_slug
  ) {
    delete pendingTrackingContext.avatar_profile_slug;
  }
  avatarSessionManager?.setTrackingContext(pendingTrackingContext);
};

export const attachAvatar = (videoElement: HTMLVideoElement): void => {
  avatarSessionManager?.attach(videoElement);
};

export const sendAvatarAudio = (base64Pcm24k: string): boolean => {
  return avatarSessionManager?.speakAudio(base64Pcm24k) || false;
};

export const interruptAvatar = (): void => {
  avatarSessionManager?.interrupt();
};

export const subscribeAvatarSpeakingState = (
  listener: (isSpeaking: boolean) => void,
): (() => void) => {
  pendingSpeakingListeners.set(listener, () => {});
  if (avatarSessionManager) {
    pendingSpeakingListeners.set(listener, avatarSessionManager.subscribeSpeakingState(listener));
  } else {
    listener(false);
  }
  return () => {
    pendingSpeakingListeners.get(listener)?.();
    pendingSpeakingListeners.delete(listener);
  };
};

export const stopAvatarSession = async (reason?: string): Promise<void> => {
  if (avatarSessionManager) {
    await avatarSessionManager.stop(reason);
  }
};

export const configureAvatarAudioPacing = (
  config: Partial<AvatarAudioPacingConfig>,
): AvatarAudioPacingConfig => {
  return avatarSessionManager?.configureAudioPacing(config) || {
    ...DEFAULT_AUDIO_PACING,
    ...config,
  };
};

export const getAvatarAudioPacingConfig = (): AvatarAudioPacingConfig => {
  return avatarSessionManager?.getAudioPacingConfig() || { ...DEFAULT_AUDIO_PACING };
};

export const getDefaultAvatarAudioPacingConfig = (): AvatarAudioPacingConfig => {
  return avatarSessionManager?.getDefaultAudioPacingConfig() || { ...DEFAULT_AUDIO_PACING };
};

export const isAvatarSandboxDevMode = (): boolean => isLiveAvatarDevMode();
